import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Loader2, ShieldAlert } from 'lucide-react';
import { adminSingleTokenContainmentAuditAPI } from '../../lib/adminSingleTokenContainmentAuditApi';
import { getErrorMessage } from '../../lib/errors';
import { useJobPolling, JobProgressLine, JobErrorBlock } from './JobPollingPanel';

const BUCKET_LABELS = {
  still_valid: 'Still valid',
  would_now_fail: 'Would now fail (suspect)',
  indeterminate: 'Indeterminate',
};

const BUCKET_VARIANTS = {
  still_valid: 'outline',
  would_now_fail: 'destructive',
  indeterminate: 'secondary',
};

// Single-Token Containment Audit (Prompt: single-token containment guard,
// 25 Aug): read-only worklist of every CONFIRMED receipt-paid match whose
// vendor evidence traced to the tier proven always-on in production before
// the fix -- services/single_token_containment_audit.py. This session can
// only ever run this against ITS OWN sandbox Mongo (CLAUDE.md section 2);
// the operator runs it from the browser against production for the real
// falsifiable check: suspect-bucket count is the number of possibly-wrong
// auto-confirms from the exposure window (0 means it was harmless there).
export function SingleTokenContainmentAuditPanel() {
  const [result, setResult] = useState(null);
  const [starting, setStarting] = useState(false);
  const [failed, setFailed] = useState(null);
  const [progress, setProgress] = useState(null);

  const poll = useJobPolling({
    fetchStatus: adminSingleTokenContainmentAuditAPI.jobStatus,
    onCompleted: useCallback((jobResult) => {
      setResult(jobResult);
      setFailed(null);
      setProgress(null);
    }, []),
    onFailed: useCallback((error) => {
      setFailed(error);
      setProgress(null);
    }, []),
    onProgress: setProgress,
  });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminSingleTokenContainmentAuditAPI.jobLatest();
        const job = data.job;
        if (!job) return;
        if (job.status === 'running') {
          poll.start(job._id || job.id);
        } else if (job.status === 'completed' && job.result) {
          setResult(job.result);
        }
      } catch {
        // Best-effort resume only.
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setStarting(true);
    setFailed(null);
    setProgress(null);
    try {
      const { data } = await adminSingleTokenContainmentAuditAPI.start();
      if (data.job_id) poll.start(data.job_id);
    } catch (err) {
      setFailed(getErrorMessage(err) || 'Failed to start audit.');
    } finally {
      setStarting(false);
    }
  };

  const busy = starting || poll.polling;
  const suspectCount = result?.buckets?.would_now_fail ?? 0;

  return (
    <Card data-testid="single-token-containment-audit-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Single-Token Containment Audit</CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          Read-only worklist of every confirmed receipt-paid match whose vendor evidence came
          through the single-token containment tier that was proven always-on before the 25 Aug
          fix. "Would now fail" rows are the suspect worklist -- possibly-wrong auto-confirms from
          the exposure window, never auto-reverted here; use the ordinary revert path per row.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          variant="outline" size="sm" onClick={run} disabled={busy}
          data-testid="single-token-containment-audit-run"
        >
          {busy ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
          Run audit
        </Button>

        {poll.polling && (
          <JobProgressLine progress={progress} testId="single-token-containment-audit-progress">
            {(p) => <>Scanning confirmed matches...</>}
          </JobProgressLine>
        )}
        <JobErrorBlock error={failed} testId="single-token-containment-audit-error" />

        {result && (
          <div className="space-y-2" data-testid="single-token-containment-audit-result">
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline" className="text-[10px]">Scanned: {result.scanned}</Badge>
              <Badge variant="outline" className="text-[10px]">In scope: {result.in_scope}</Badge>
              {Object.entries(result.buckets ?? {}).map(([bucket, count]) => (
                <Badge
                  key={bucket} variant={BUCKET_VARIANTS[bucket] || 'outline'} className="text-[10px]"
                  data-testid={`single-token-containment-audit-bucket-${bucket}`}
                >
                  {BUCKET_LABELS[bucket] || bucket}: {count}
                </Badge>
              ))}
              {result.registered_vendor_share !== null && result.registered_vendor_share !== undefined && (
                <Badge variant="outline" className="text-[10px]">
                  Already-registered vendor share: {Math.round(result.registered_vendor_share * 100)}%
                </Badge>
              )}
            </div>

            <p className="text-xs font-medium" data-testid="single-token-containment-audit-suspect-summary">
              {suspectCount === 0
                ? 'Falsifiable check: 0 suspects -- the exposure was harmless in this population.'
                : `Falsifiable check: ${suspectCount} suspect(s) -- possibly-wrong auto-confirm(s), review below.`}
            </p>

            {result.rows?.length > 0 && (
              <table className="w-full text-[10px]" data-testid="single-token-containment-audit-rows">
                <thead>
                  <tr className="text-left text-muted-foreground">
                    <th className="pr-2">Vendor</th>
                    <th className="pr-2">Transaction text</th>
                    <th className="pr-2">Token</th>
                    <th className="pr-2">Bucket</th>
                    <th className="pr-2">Match</th>
                  </tr>
                </thead>
                <tbody>
                  {result.rows.map((row) => (
                    <tr key={row.match_id} className="border-t" data-testid={`single-token-containment-audit-row-${row.match_id}`}>
                      <td className="pr-2 py-1">{row.vendor_name || '--'}</td>
                      <td className="pr-2 py-1">{row.tx_text || '--'}</td>
                      <td className="pr-2 py-1">{row.containment_token || '--'}</td>
                      <td className="pr-2 py-1">
                        <Badge variant={BUCKET_VARIANTS[row.bucket] || 'outline'} className="text-[10px]">
                          {BUCKET_LABELS[row.bucket] || row.bucket}
                        </Badge>
                      </td>
                      <td className="py-1">
                        <a
                          href={`/matches?match_id=${row.match_id}`} target="_blank" rel="noreferrer"
                          className="underline"
                        >
                          {row.match_id?.slice(0, 8)}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
